import React from "react";
import { ArrowRight, MessageCircle, Globe2, ShieldCheck } from "lucide-react";
import { WHATSAPP_LINK } from "../../data/programs";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero"
      className="relative isolate overflow-hidden bg-grain pt-12 sm:pt-16 pb-20 md:pb-28"
    >
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-cover bg-center opacity-[0.18]"
        style={{
          backgroundImage:
            'url("https://images.unsplash.com/photo-1567562227343-a72d22e187c8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920")',
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-aiou-paper/88 backdrop-blur-[2px]"
      />

      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* LEFT */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white border border-aiou-sand shadow-sm overflow-hidden">
                <img
                  src="/aiou-logo.png"
                  alt="AIOU"
                  className="h-12 w-12 object-contain"
                  data-testid="hero-logo"
                />
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-aiou-sand bg-white/70 px-4 py-1.5 text-xs tracking-wider uppercase text-aiou-slate shadow-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-aiou-gold" />
                <span>Allama Iqbal Open University · International Students</span>
              </div>
            </div>

            <h1
              data-testid="hero-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-aiou-ink leading-[1.04] tracking-tight"
            >
              Study with{" "}
              <span className="relative inline-block">
                <span className="relative z-10">AIOU</span>
                <span
                  aria-hidden
                  className="absolute inset-x-0 bottom-1 h-3 bg-aiou-gold/35 -z-0 rounded-sm"
                />
              </span>{" "}
              from anywhere in the world — with free, personal guidance.
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-6 max-w-2xl text-base sm:text-lg text-aiou-slate leading-relaxed"
            >
              Pakistan's largest open university brings you world-class online
              programs — Matric to Master's. Kamran Mir, an AIOU alumnus based
              in Europe, helps international students apply and process their
              admission, completely <span className="font-semibold text-aiou-steel">free of charge</span>.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href={WHATSAPP_LINK()}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-whatsapp-btn"
                className="inline-flex items-center gap-2 rounded-full bg-aiou-steel text-white px-6 py-3.5 text-base font-medium shadow-lg shadow-aiou-steel/20 hover:bg-aiou-steelDark transition-all"
              >
                <MessageCircle className="h-5 w-5" strokeWidth={1.7} />
                Get Free Guidance on WhatsApp
              </a>
              <a
                href="#programs"
                data-testid="hero-programs-btn"
                className="inline-flex items-center gap-2 rounded-full border-2 border-aiou-steel/20 bg-white/70 px-6 py-3 text-base text-aiou-steel hover:border-aiou-gold hover:text-aiou-steelDark transition-colors"
              >
                Explore programs
                <ArrowRight className="h-4 w-4" strokeWidth={1.7} />
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm">
              <Trust icon={<ShieldCheck className="h-4 w-4" />} text="HEC-recognised degrees" />
              <Trust icon={<Globe2 className="h-4 w-4" />} text="Students in 35+ countries" />
              <Trust icon={<MessageCircle className="h-4 w-4" />} text="Free 1-on-1 support" />
            </div>
          </div>

          {/* RIGHT — Kamran card */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 rounded-[28px] bg-aiou-gold/15 blur-2xl" />
              <div className="relative rounded-3xl border border-aiou-sand bg-white/90 backdrop-blur-xl p-7 sm:p-8 shadow-[0_18px_50px_rgba(36,66,101,0.12)]">
                <div className="flex items-start gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-aiou-steel text-white font-serif text-2xl shadow-md">
                    KM
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs tracking-wider uppercase text-aiou-gold font-semibold">
                      Free guidance · Europe
                    </div>
                    <div className="mt-1 font-serif text-2xl text-aiou-ink leading-tight">
                      Kamran Mir
                    </div>
                    <div className="text-sm text-aiou-slate">
                      AIOU alumnus · Volunteer mentor
                    </div>
                  </div>
                </div>

                <p className="mt-5 text-[15px] text-aiou-slate leading-relaxed">
                  "I help international students apply to AIOU step-by-step —
                  choosing the right program, filling the online form, paying
                  fees from abroad, and attesting documents. Free of charge."
                </p>

                <div className="mt-6 space-y-2.5">
                  <a
                    href={WHATSAPP_LINK()}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="hero-kamran-whatsapp"
                    className="flex items-center justify-between gap-3 rounded-xl border border-aiou-sand bg-aiou-paper/60 px-4 py-3 hover:bg-emerald-50 hover:border-emerald-200 transition-colors group"
                  >
                    <span className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600">
                        <MessageCircle className="h-4 w-4" strokeWidth={1.8} />
                      </span>
                      <span>
                        <span className="block text-[11px] uppercase tracking-wider text-aiou-mute">
                          WhatsApp
                        </span>
                        <span className="block text-aiou-ink font-medium text-sm">
                          +353 85 151 1839
                        </span>
                      </span>
                    </span>
                    <ArrowRight
                      className="h-4 w-4 text-aiou-slate group-hover:translate-x-0.5 transition-transform"
                      strokeWidth={1.7}
                    />
                  </a>
                  <a
                    href="mailto:kamranmir99@gmail.com"
                    data-testid="hero-kamran-email"
                    className="flex items-center justify-between gap-3 rounded-xl border border-aiou-sand bg-aiou-paper/60 px-4 py-3 hover:bg-aiou-gold/5 hover:border-aiou-gold/40 transition-colors group"
                  >
                    <span className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-aiou-gold/15 text-aiou-goldDark">
                        ✉
                      </span>
                      <span>
                        <span className="block text-[11px] uppercase tracking-wider text-aiou-mute">
                          Email
                        </span>
                        <span className="block text-aiou-ink font-medium text-sm">
                          kamranmir99@gmail.com
                        </span>
                      </span>
                    </span>
                    <ArrowRight
                      className="h-4 w-4 text-aiou-slate group-hover:translate-x-0.5 transition-transform"
                      strokeWidth={1.7}
                    />
                  </a>
                </div>

                <div className="mt-5 text-[11px] text-aiou-mute leading-relaxed">
                  Independent volunteer mentor. Not an agent — no commission, no
                  fees. All applications go through the official AIOU portal.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Trust = ({ icon, text }) => (
  <div className="inline-flex items-center gap-2 text-aiou-slate">
    <span className="text-aiou-steel">{icon}</span>
    <span>{text}</span>
  </div>
);
